import { Component, Input, EventEmitter, Output } from "@angular/core";
import { MaiaChatService } from "../../data-access-layer/maia/maia-chat.service";
import { MaiaChatMessage, MaiaChatRole } from "../../model/maia/maia-chat-message";

/**
 * MAIA Entry Page — left panel.
 * Design reference: docs/AI/Entry Page.png
 *
 * Holds the conversation: the static greeting plus whatever the user and
 * MAIA exchange via `MaiaChatService.ask`. `jobId` (when set by the parent
 * wizard) is passed along so answers can be grounded in the SOV job the user
 * is currently working on; it's optional so the panel still works before a
 * job exists (e.g. on the bare entry page).
 */
@Component({
    selector: "maia-chat-panel",
    templateUrl: "./maia-chat-panel.component.html",
    styleUrls: ["./maia-chat-panel.component.scss"],
})
export class MaiaChatPanelComponent {
    /** The SOV job the conversation should be grounded in, if one exists yet. */
    @Input() jobId: number | null = null;

    /** Raised by the "Upload new SOV file" action. */
    @Output() readonly uploadRequested = new EventEmitter<void>();

    /** Raised by the "Exit" action in the panel header. */
    @Output() readonly exitRequested = new EventEmitter<void>();

    readonly MaiaChatRole = MaiaChatRole;

    readonly greeting =
        "Hello! I am MAIA, your Maptycs AI Assistant. I can help you upload your data, " +
        "geocode your locations, and map your assets. Let's get started.";

    messages: MaiaChatMessage[] = [];
    draft = "";
    sending = false;
    errorMessage: string | null = null;

    constructor(private readonly chatService: MaiaChatService) {}

    send(): void {
        const question = this.draft.trim();
        if (!question || this.sending) {
            return;
        }

        this.messages.push({ role: MaiaChatRole.USER, text: question });
        this.draft = "";
        this.sending = true;
        this.errorMessage = null;

        this.chatService.ask(this.jobId, question).subscribe({
            next: (answer) => {
                this.messages.push({ role: MaiaChatRole.ASSISTANT, text: answer.answer });
                this.sending = false;
            },
            error: () => {
                this.errorMessage = "MAIA couldn't answer that just now. Please try again.";
                this.sending = false;
            },
        });
    }
}
